import { useState } from "react";
import { MapPin, Facebook } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!form.name.trim() || !emailOk || !form.message.trim()) {
      toast.error("Please fill in your name, a valid email, and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message has been sent. We'll be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] pt-24 pb-10 border-t border-[#2B2825]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 pb-16 border-b border-[#2B2825]">
          <div className="md:col-span-1">
            <img
              src="https://customer-assets.emergentagent.com/job_sabbath-way/artifacts/kaufvsti_ChatGPT%20Image%20Jul%201%2C%202026%2C%2005_36_32%20PM.png"
              alt="The Restoration"
              className="h-28 w-auto object-contain mb-6"
            />
            <p className="text-sm leading-relaxed font-light text-[#A39E98] max-w-xs">
              Restoring the original gospel of Christ. Putting back in what they
              took out.
            </p>
            <div className="mt-6 flex items-center gap-3 text-sm text-[#A39E98]">
              <MapPin strokeWidth={1} className="h-4 w-4 text-[#D4AF37]" />
              A worldwide fellowship
            </div>
            <a
              href="https://www.facebook.com/restorationpodcast"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              aria-label="Follow us on Facebook"
              onClick={() => trackEvent("click_social", { network: "facebook" })}
              className="mt-6 inline-flex h-11 w-11 items-center justify-center border border-[#4A453F] text-[#F4F0EA] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <Facebook strokeWidth={1.5} className="h-5 w-5" />
            </a>
          </div>

          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-6">Explore</p>
            <ul className="space-y-3">
              {[
                { l: "The Teachings", h: "#teachings" },
                { l: "The Word", h: "#scripture" },
                { l: "Our Stand", h: "#about" },
                { l: "Join Us", h: "#join" },
              ].map((x) => (
                <li key={x.h}>
                  <a href={x.h} className="text-sm text-[#A39E98] hover:text-[#F4F0EA] transition-colors">
                    {x.l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-6">Reach Us</p>
            <form data-testid="contact-form" noValidate onSubmit={onSubmit} className="space-y-3">
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Your name"
                data-testid="contact-name-input"
                className="w-full bg-[#141311] border border-[#2B2825] text-[#F4F0EA] placeholder:text-[#A39E98]/60 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
              />
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="your@email.com"
                data-testid="contact-email-input"
                className="w-full bg-[#141311] border border-[#2B2825] text-[#F4F0EA] placeholder:text-[#A39E98]/60 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
              />
              <textarea
                value={form.message}
                onChange={update("message")}
                placeholder="Your message"
                rows={3}
                data-testid="contact-message-input"
                className="w-full bg-[#141311] border border-[#2B2825] text-[#F4F0EA] placeholder:text-[#A39E98]/60 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="w-full bg-[#D4AF37] text-[#0A0A0A] font-medium px-6 py-3 text-sm hover:bg-[#E5C865] transition-colors disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>

        <div className="pt-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-heading italic text-2xl md:text-4xl text-[#F4F0EA]/90 text-center md:text-left">
            "Contend earnestly for the faith once delivered."
          </p>
          <a
            href="https://www.tvbeyth-el.net"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-learn-more"
            onClick={() => trackEvent("click_learn_more", { location: "footer", link_url: "https://www.tvbeyth-el.net" })}
            className="inline-flex items-center gap-2 whitespace-nowrap bg-[#D4AF37] text-[#0A0A0A] font-medium px-6 py-3 hover:bg-[#E5C865] transition-colors"
          >
            To Learn more, click here.
          </a>
        </div>

        <p className="mt-10 text-xs text-[#A39E98]/60 font-mono">
          © {new Date().getFullYear()} The Restoration. All glory to God.
        </p>
      </div>
    </footer>
  );
};
